import { useEffect, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import cookie from 'react-cookies'
import { requestUserLogout } from '@/store/actions/user/logout'
import { requestUserPing } from '@/store/actions/user/userPing'
import createNotifications from '@/helpers/notification'
import { ACTIVE_PING_ITEM, ACCESS_TOKEN_COOKIE } from '@/constants/names'
import emitter from '@/helpers/eventEmitter'

const MS_PER_MINUTE = 60 * 1000
const DEFAULT_TIMEOUT = 15 * MS_PER_MINUTE
const DEFAULT_INTERVAL_TIMEOUT = MS_PER_MINUTE
const PING_BEFORE_EXPIRY_MS = 60 * 1000

const getHiddenName = () => {
  let name
  if (typeof document.hidden !== 'undefined') {
    name = 'hidden'
  }
  if (typeof document.msHidden !== 'undefined') {
    name = 'msHidden'
  }
  if (typeof document.webkitHidden !== 'undefined') {
    name = 'webkitHidden'
  }

  return name
}

const VISIBILITY_BY_HIDDEN = {
  hidden: 'visibilitychange',
  msHidden: 'msvisibilitychange',
  webkitHidden: 'webkitvisibilitychange',
}

const hidden = getHiddenName()

export const useHandleActivity = (
  timeout = DEFAULT_TIMEOUT,
  intervalTimeout = DEFAULT_INTERVAL_TIMEOUT,
) => {
  const dispatch = useDispatch()
  const blurStartTimeRef = useRef(null)

  const requestPing = useCallback(() => {
    const lifeTime = cookie.load('lifeTime')
    const startTime = cookie.load('startTime')
    if (startTime == null || lifeTime == null) {
      return
    }
    const timeDiff = Date.now() - startTime
    const canCallPingByTime = timeDiff >= lifeTime * 1000 - PING_BEFORE_EXPIRY_MS
    if (canCallPingByTime) {
      window.localStorage.setItem(`${ACTIVE_PING_ITEM}:time`, `${Date.now()}`)
      dispatch(requestUserPing())
    }
  }, [dispatch])

  useEffect(() => {
    if (!hidden) {
      return undefined
    }

    const toast = createNotifications()

    const handleVisibilityChange = () => {
      if (!document[hidden]) {
        blurStartTimeRef.current = null
        requestPing()
      } else {
        blurStartTimeRef.current = Date.now()
        window.localStorage.setItem('sls:handle-start', `${new Date()}`)
      }
    }

    const tryPingWhenIdle = () => {
      const hasPingRequest = window.localStorage.getItem(ACTIVE_PING_ITEM) === '1'
      if (!hasPingRequest) {
        requestPing()
      }
    }

    const onActivityIdle = (_, events) => {
      if (events[ACTIVE_PING_ITEM] === 0) {
        tryPingWhenIdle()
      }
    }

    emitter.subscribe([ACTIVE_PING_ITEM], onActivityIdle)

    document.addEventListener(VISIBILITY_BY_HIDDEN[hidden], handleVisibilityChange, false)

    const intervalId = window.setInterval(() => {
      const accessToken = cookie.load(ACCESS_TOKEN_COOKIE)

      if (!accessToken) {
        clearInterval(intervalId)
        return
      }

      if (document[hidden]) {
        if (blurStartTimeRef.current === null) {
          blurStartTimeRef.current = Date.now()
          window.localStorage.setItem('sls:handle-start', `${new Date()}`)
        } else {
          const timeDiffSec = Math.floor(
            (Date.now() - blurStartTimeRef.current) / 1000,
          )
          const timeoutMinLevelSec = (timeout - intervalTimeout) / 1000
          if (timeDiffSec >= timeoutMinLevelSec) {
            dispatch(requestUserLogout())
            window.localStorage.setItem('sls:handle-end', `${new Date()}`)
            clearInterval(intervalId)
            toast({
              title: 'ЗАВЕРШЕНИЕ СЕССИИ!',
              description: 'Сессия завершена из-за длительной неактивности. Для работы с системой необходима повторная авторизация',
              type: 'error',
            }, { autoClose: false })
          }
        }
      } else {
        blurStartTimeRef.current = null
        requestPing()
      }
    }, intervalTimeout)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener(VISIBILITY_BY_HIDDEN[hidden], handleVisibilityChange, false)
    }
  }, [timeout, intervalTimeout, dispatch, requestPing])

  return null
}

export default useHandleActivity
